import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import {
    FiShield,
    FiDatabase,
    FiSettings,
    FiUsers,
    FiLock,
    FiCheckCircle,
    FiUserCheck,
    FiRefreshCw,
    FiMail,
    FiGlobe,
    FiArrowRight,
    FiArrowUp,
} from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import { TbCookie } from "react-icons/tb";
import ClientNavbar from "../components/Navbar/ClinetNavbar";
import FixedButton from "../components/Client/FixedButton";
import ClientFooter from "../components/Client/ClientFooter";
import ClientAdSense from "../components/Client/ClientAdSense";

const SECTIONS = [
    { id: "intro", label: "Introduction", icon: FiShield },
    { id: "info", label: "Information We Collect", icon: FiDatabase },
    { id: "use", label: "How We Use Data", icon: FiSettings },
    { id: "thirdparty", label: "Third-Party Services", icon: FiUsers },
    { id: "cookies", label: "Cookies & Tracking", icon: TbCookie },
    { id: "security", label: "Data Security", icon: FiLock },
    { id: "rights", label: "Your Rights", icon: FiCheckCircle },
    { id: "children", label: "Children's Privacy", icon: FiUserCheck },
    { id: "updates", label: "Policy Updates", icon: FiRefreshCw },
    { id: "contact", label: "Contact Us", icon: FiMail },
];

const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const SectionShell = ({ id, num, icon: Icon, title, children }) => (
    <motion.section
        id={id}
        {...fadeUp}
        className='ios-glass-dark relative rounded-3xl p-5 md:p-8 overflow-hidden scroll-mt-28'
        style={{
            boxShadow:
                "0 14px 36px -14px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.1)",
        }}
    >
        <div
            aria-hidden
            className='absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-15 blur-3xl pointer-events-none'
            style={{
                background:
                    "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
            }}
        />
        <div className='relative flex items-center gap-3 mb-5'>
            <div
                className='w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center flex-shrink-0 relative'
                style={{
                    background:
                        "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                    boxShadow:
                        "0 8px 22px -6px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
                }}
            >
                <Icon className='text-lg md:text-xl text-white' />
            </div>
            <div className='min-w-0'>
                <p className='text-client-title text-[10px] md:text-xs uppercase tracking-[0.18em] font-semibold'>
                    Section {num}
                </p>
                <h2 className='text-client-font font-bold text-lg md:text-2xl tracking-tight leading-tight'>
                    {title}
                </h2>
            </div>
        </div>
        <div className='relative text-client-font/75 text-sm md:text-base leading-relaxed space-y-4'>
            {children}
        </div>
    </motion.section>
);

const Bullet = ({ children }) => (
    <li className='flex items-start gap-2.5'>
        <span
            className='mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0'
            style={{
                background: "linear-gradient(135deg, #7F00FF, #FCFA02)",
                boxShadow: "0 0 6px rgba(127,0,255,0.5)",
            }}
        />
        <span>{children}</span>
    </li>
);

const CheckCard = ({ children }) => (
    <div
        className='flex items-start gap-2.5 rounded-xl p-3 md:p-4'
        style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
        }}
    >
        <div
            className='mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0'
            style={{
                background: "linear-gradient(135deg, #FCFA02, #FF8AFF)",
            }}
        >
            <span className='text-[10px] font-bold text-client-back'>✓</span>
        </div>
        <span className='text-client-font/80 text-sm'>{children}</span>
    </div>
);

const Note = ({ children }) => (
    <div
        className='rounded-xl p-4'
        style={{
            background:
                "linear-gradient(135deg, rgba(127,0,255,0.12), rgba(252,250,2,0.04))",
            border: "1px solid rgba(127,0,255,0.25)",
        }}
    >
        <p className='text-client-font/85 text-sm leading-relaxed'>{children}</p>
    </div>
);

const ServiceCard = ({ title, children }) => (
    <motion.div
        whileHover={{ y: -3, scale: 1.01 }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
        className='rounded-2xl p-4 md:p-5'
        style={{
            background:
                "linear-gradient(135deg, rgba(127,0,255,0.16), rgba(127,0,255,0.04))",
            border: "1px solid rgba(127,0,255,0.3)",
            boxShadow:
                "0 8px 22px -8px rgba(127,0,255,0.35), inset 0 1px 0 rgba(255,255,255,0.1)",
        }}
    >
        <h3
            className='font-bold text-base md:text-lg mb-1.5 inline-flex items-center gap-2'
            style={{ color: "#FCFA02" }}
        >
            <span
                className='w-1.5 h-1.5 rounded-full inline-block'
                style={{ background: "#FCFA02" }}
            />
            {title}
        </h3>
        <p className='text-client-font/75 text-sm leading-relaxed'>{children}</p>
    </motion.div>
);

const ExternalLink = ({ href, children }) => (
    <a
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className='font-semibold underline decoration-2 underline-offset-2 transition-colors'
        style={{ color: "#FCFA02" }}
    >
        {children}
    </a>
);

const PP = () => {
    const [activeSection, setActiveSection] = useState("intro");

    useEffect(() => {
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActiveSection(e.target.id);
                });
            },
            { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
        );
        SECTIONS.forEach((s) => {
            const el = document.getElementById(s.id);
            if (el) obs.observe(el);
        });
        return () => obs.disconnect();
    }, []);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className='min-h-screen dm-sans client-home-bg'>
            <ClientNavbar />
            <FixedButton />

            {/* ===== HERO ===== */}
            <section className='relative w-full overflow-hidden'>
                <motion.div
                    aria-hidden
                    animate={{ x: [0, 40, -20, 0], y: [0, -30, 30, 0] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute -top-10 -left-20 w-80 h-80 rounded-full opacity-35 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                    }}
                />
                <motion.div
                    aria-hidden
                    animate={{ x: [0, -40, 30, 0], y: [0, 30, -40, 0] }}
                    transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
                    className='absolute top-10 -right-20 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none'
                    style={{
                        background:
                            "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                    }}
                />

                <div className='relative mx-auto max-w-5xl px-4 sm:px-6 md:px-8 pt-8 md:pt-14 pb-6 md:pb-10 text-center'>
                    {/* DSPY1 — top-of-page display ad */}
                    <ClientAdSense
                        slot='9729451986'
                        containerClassName='w-full mb-6 md:mb-10 text-left'
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className='inline-flex items-center gap-2 ios-glass px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/85 mb-4'>
                            <HiSparkles style={{ color: "#FCFA02" }} />
                            <span className='tracking-wider uppercase'>
                                Legal
                            </span>
                        </div>
                        <h1 className='text-4xl md:text-6xl font-bold text-client-font tracking-tight leading-tight'>
                            Privacy{" "}
                            <span
                                style={{
                                    background:
                                        "linear-gradient(90deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                Policy
                            </span>
                        </h1>
                        <p className='text-client-font/60 text-sm md:text-lg mt-4 max-w-2xl mx-auto leading-relaxed'>
                            How we collect, use, and protect your data when you use
                            PicPromptify — written clearly, no legalese.
                        </p>
                        <div className='inline-flex items-center gap-2 mt-5 px-3 py-1.5 rounded-full text-[10px] md:text-xs text-client-font/75'
                            style={{
                                background: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.1)",
                            }}>
                            <FiRefreshCw className='text-[11px]' />
                            Last updated: January 2026
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ===== CONTENT ===== */}
            <section className='relative w-full px-4 sm:px-6 md:px-8 pb-12 md:pb-20'>
                <div className='mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10'>
                    {/* TOC sidebar */}
                    <aside className='lg:col-span-4 xl:col-span-3 lg:sticky lg:top-24 lg:self-start'>
                        <div
                            className='rounded-3xl p-4 md:p-5'
                            style={{
                                background: "rgba(255,255,255,0.04)",
                                backdropFilter: "blur(18px) saturate(180%)",
                                WebkitBackdropFilter: "blur(18px) saturate(180%)",
                                border: "1px solid rgba(255,255,255,0.1)",
                                boxShadow:
                                    "0 14px 36px -16px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)",
                            }}
                        >
                            <p className='text-client-title text-[10px] uppercase tracking-[0.2em] font-semibold mb-3'>
                                On this page
                            </p>
                            <nav className='space-y-1'>
                                {SECTIONS.map((s, i) => {
                                    const Icon = s.icon;
                                    const active = activeSection === s.id;
                                    return (
                                        <motion.button
                                            key={s.id}
                                            whileHover={{ x: 4 }}
                                            onClick={() => scrollTo(s.id)}
                                            className='relative w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition-colors'
                                            style={{
                                                background: active
                                                    ? "linear-gradient(135deg, rgba(127,0,255,0.25), rgba(252,250,2,0.06))"
                                                    : "transparent",
                                                border: active
                                                    ? "1px solid rgba(127,0,255,0.4)"
                                                    : "1px solid transparent",
                                                color: active
                                                    ? "#FCFA02"
                                                    : "rgba(255,255,255,0.6)",
                                            }}
                                        >
                                            <Icon className='text-sm flex-shrink-0' />
                                            <span className='text-[12px] md:text-xs font-medium flex-1 truncate'>
                                                {i + 1}. {s.label}
                                            </span>
                                            {active && (
                                                <FiArrowRight
                                                    className='text-xs flex-shrink-0'
                                                    style={{ color: "#FCFA02" }}
                                                />
                                            )}
                                        </motion.button>
                                    );
                                })}
                            </nav>
                        </div>
                    </aside>

                    {/* Main content */}
                    <div className='lg:col-span-8 xl:col-span-9 space-y-5 md:space-y-6'>
                        <SectionShell id='intro' num='1' icon={FiShield} title='Introduction'>
                            <p>
                                At <span className='text-client-font font-semibold'>PicPromptify</span>, we
                                respect your privacy and are transparent about how we handle your data. This
                                Privacy Policy explains what information we collect when you use our website,
                                why we collect it, and how we protect it — especially as we use Firebase,
                                Google Analytics, and Google AdSense to operate and improve our service.
                            </p>
                            <p>
                                By using{" "}
                                <ExternalLink href='https://picpromptify.com/'>
                                    picpromptify.com
                                </ExternalLink>
                                , you consent to the practices described in this policy.
                            </p>
                        </SectionShell>

                        <SectionShell id='info' num='2' icon={FiDatabase} title='Information We Collect'>
                            <div>
                                <h3 className='text-client-title font-semibold mb-2'>
                                    a. Data You Provide
                                </h3>
                                <ul className='space-y-2'>
                                    <Bullet>Text prompts you enter to generate AI image ideas</Bullet>
                                    <Bullet>Optional email address (e.g., for updates or support)</Bullet>
                                </ul>
                            </div>
                            <div>
                                <h3 className='text-client-title font-semibold mb-2'>
                                    b. Automatically Collected Data
                                </h3>
                                <p className='mb-3'>
                                    When you visit our site, we and third-party services may automatically collect:
                                </p>
                                <ul className='space-y-2 mb-4'>
                                    <Bullet>IP address</Bullet>
                                    <Bullet>Browser type and version</Bullet>
                                    <Bullet>Operating system</Bullet>
                                    <Bullet>Pages visited, time spent, and referral sources</Bullet>
                                    <Bullet>Device identifiers and usage data</Bullet>
                                </ul>
                                <p className='mb-2'>This data is collected via:</p>
                                <ul className='space-y-2 mb-4'>
                                    <Bullet>Firebase (for site functionality and analytics)</Bullet>
                                    <Bullet>Google Analytics (to understand user behavior)</Bullet>
                                    <Bullet>Google AdSense (to serve personalized ads)</Bullet>
                                </ul>
                                <Note>
                                    We do not store the images you generate using external AI tools (like
                                    DALL·E or Midjourney). Prompts are processed in real time and not saved
                                    permanently unless needed for security or service improvement.
                                </Note>
                            </div>
                        </SectionShell>

                        <SectionShell id='use' num='3' icon={FiSettings} title='How We Use Your Information'>
                            <p>We use collected data to:</p>
                            <div className='grid sm:grid-cols-2 gap-2.5'>
                                <CheckCard>Deliver and optimize our AI image prompt generator</CheckCard>
                                <CheckCard>Analyze traffic and engagement via Firebase & Google Analytics</CheckCard>
                                <CheckCard>Serve relevant ads through Google AdSense</CheckCard>
                                <CheckCard>Prevent abuse, fraud, and technical issues</CheckCard>
                                <CheckCard>Comply with legal obligations</CheckCard>
                            </div>
                        </SectionShell>

                        <SectionShell id='thirdparty' num='4' icon={FiUsers} title='Third-Party Services & Data Sharing'>
                            <p>We use the following third-party services that may collect data:</p>
                            <div className='space-y-3'>
                                <ServiceCard title='Google Firebase'>
                                    Used for hosting, authentication (if applicable), and analytics. Firebase
                                    may collect device and usage data as per{" "}
                                    <ExternalLink href='https://policies.google.com/privacy'>
                                        Google's Privacy Policy
                                    </ExternalLink>
                                    .
                                </ServiceCard>
                                <ServiceCard title='Google Analytics'>
                                    Helps us understand how visitors interact with PicPromptify. Data is
                                    anonymized where possible. Learn more at{" "}
                                    <ExternalLink href='https://support.google.com/analytics/answer/6004245'>
                                        Google Analytics Privacy
                                    </ExternalLink>
                                    .
                                </ServiceCard>
                                <ServiceCard title='Google AdSense'>
                                    Displays ads to support our free service. AdSense uses cookies to
                                    personalize ads based on your browsing activity. You can opt out via{" "}
                                    <ExternalLink href='https://adssettings.google.com/'>
                                        Google's Ad Settings
                                    </ExternalLink>
                                    .
                                </ServiceCard>
                            </div>
                            <Note>
                                We do not sell your personal data. However, these third parties may use your
                                data in accordance with their own privacy policies.
                            </Note>
                        </SectionShell>

                        <SectionShell id='cookies' num='5' icon={TbCookie} title='Cookies and Tracking Technologies'>
                            <p>
                                Our site uses cookies and similar technologies (via Firebase, Analytics, and
                                AdSense) to:
                            </p>
                            <div className='grid sm:grid-cols-3 gap-3'>
                                {[
                                    { emoji: "🍪", label: "Remember preferences" },
                                    { emoji: "📊", label: "Analyze site performance" },
                                    { emoji: "🎯", label: "Deliver targeted advertising" },
                                ].map((c) => (
                                    <div
                                        key={c.label}
                                        className='rounded-xl p-4 text-center'
                                        style={{
                                            background: "rgba(255,255,255,0.04)",
                                            border: "1px solid rgba(255,255,255,0.1)",
                                        }}
                                    >
                                        <div className='text-2xl mb-1'>{c.emoji}</div>
                                        <p className='text-client-font/85 text-xs md:text-sm font-medium'>
                                            {c.label}
                                        </p>
                                    </div>
                                ))}
                            </div>
                            <Note>
                                You can manage or disable cookies through your browser settings, though this
                                may affect site functionality.
                            </Note>
                        </SectionShell>

                        <SectionShell id='security' num='6' icon={FiLock} title='Data Security'>
                            <p>
                                We implement reasonable technical and organizational measures to protect your
                                data. However, no internet transmission is 100% secure — please use our
                                service with awareness.
                            </p>
                        </SectionShell>

                        <SectionShell id='rights' num='7' icon={FiCheckCircle} title='Your Rights'>
                            <p>
                                Depending on your region (e.g., under GDPR or CCPA), you may have the right to:
                            </p>
                            <div className='grid sm:grid-cols-2 gap-2.5'>
                                <CheckCard>Access, correct, or delete your personal data</CheckCard>
                                <CheckCard>Object to or restrict processing</CheckCard>
                                <CheckCard>Opt out of personalized ads</CheckCard>
                                <CheckCard>Withdraw consent at any time</CheckCard>
                            </div>
                            <p>
                                To exercise these rights, contact us at:{" "}
                                <a
                                    href='mailto:picpromptify@gmail.com'
                                    className='font-semibold underline decoration-2 underline-offset-2'
                                    style={{ color: "#FCFA02" }}
                                >
                                    picpromptify@gmail.com
                                </a>
                            </p>
                        </SectionShell>

                        <SectionShell id='children' num='8' icon={FiUserCheck} title="Children's Privacy">
                            <p>
                                PicPromptify is not directed at children under 13. We do not knowingly collect
                                their data. If we become aware of such collection, we will delete it promptly.
                            </p>
                        </SectionShell>

                        <SectionShell id='updates' num='9' icon={FiRefreshCw} title='Policy Updates'>
                            <p>
                                We may update this Privacy Policy occasionally. The revised version will be
                                posted here with a new "Last Updated" date. Continued use of the site means
                                you accept the changes.
                            </p>
                        </SectionShell>

                        {/* Contact (highlighted) */}
                        <motion.section
                            id='contact'
                            {...fadeUp}
                            className='relative rounded-3xl p-5 md:p-8 overflow-hidden scroll-mt-28'
                            style={{
                                background:
                                    "linear-gradient(135deg, rgba(127,0,255,0.2), rgba(252,250,2,0.06))",
                                border: "1px solid rgba(127,0,255,0.35)",
                                backdropFilter: "blur(18px) saturate(180%)",
                                WebkitBackdropFilter: "blur(18px) saturate(180%)",
                                boxShadow:
                                    "0 18px 40px -12px rgba(127,0,255,0.45), inset 0 1px 0 rgba(255,255,255,0.14)",
                            }}
                        >
                            <motion.div
                                aria-hidden
                                animate={{ rotate: 360 }}
                                transition={{
                                    duration: 40,
                                    repeat: Infinity,
                                    ease: "linear",
                                }}
                                className='absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-25 blur-3xl pointer-events-none'
                                style={{
                                    background:
                                        "conic-gradient(from 0deg, #7F00FF, #FF8AFF, #FCFA02, #7F00FF)",
                                }}
                            />

                            <div className='relative flex items-center gap-3 mb-5'>
                                <div
                                    className='w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0'
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #FCFA02, #FF8AFF, #7F00FF)",
                                        boxShadow:
                                            "0 12px 28px -8px rgba(127,0,255,0.6)",
                                    }}
                                >
                                    <FiMail className='text-xl text-white' />
                                </div>
                                <div>
                                    <p className='text-client-title text-[10px] md:text-xs uppercase tracking-[0.18em] font-semibold'>
                                        Section 10
                                    </p>
                                    <h2 className='text-client-font font-bold text-lg md:text-2xl tracking-tight leading-tight'>
                                        Contact Us
                                    </h2>
                                </div>
                            </div>
                            <p className='relative text-client-font/80 text-sm md:text-base mb-5'>
                                Have questions about your privacy? Reach out and we'll get back to you.
                            </p>
                            <div className='relative grid sm:grid-cols-2 gap-3'>
                                <motion.a
                                    whileHover={{ y: -3, scale: 1.02 }}
                                    whileTap={{ scale: 0.97 }}
                                    href='mailto:picpromptify@gmail.com'
                                    className='flex items-center gap-3 rounded-2xl p-4'
                                    style={{
                                        background: "rgba(255,255,255,0.05)",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        backdropFilter: "blur(14px)",
                                    }}
                                >
                                    <div
                                        className='w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0'
                                        style={{
                                            background:
                                                "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                        }}
                                    >
                                        <FiMail className='text-base text-white' />
                                    </div>
                                    <div className='min-w-0'>
                                        <p className='text-client-font/55 text-[10px] uppercase tracking-wider'>
                                            Email
                                        </p>
                                        <p className='text-client-font font-semibold text-sm truncate'>
                                            picpromptify@gmail.com
                                        </p>
                                    </div>
                                </motion.a>
                                <motion.a
                                    whileHover={{ y: -3, scale: 1.02 }}
                                    whileTap={{ scale: 0.97 }}
                                    href='https://picpromptify.com/'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='flex items-center gap-3 rounded-2xl p-4'
                                    style={{
                                        background: "rgba(255,255,255,0.05)",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        backdropFilter: "blur(14px)",
                                    }}
                                >
                                    <div
                                        className='w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0'
                                        style={{
                                            background:
                                                "linear-gradient(135deg, #FCFA02, #FF8AFF)",
                                        }}
                                    >
                                        <FiGlobe className='text-base text-white' />
                                    </div>
                                    <div className='min-w-0'>
                                        <p className='text-client-font/55 text-[10px] uppercase tracking-wider'>
                                            Website
                                        </p>
                                        <p className='text-client-font font-semibold text-sm truncate'>
                                            picpromptify.com
                                        </p>
                                    </div>
                                </motion.a>
                            </div>
                        </motion.section>

                        {/* Back to top */}
                        <div className='flex justify-center pt-2'>
                            <motion.button
                                whileHover={{ y: -3, scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                                className='ios-glass-btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-client-font text-xs md:text-sm font-medium'
                            >
                                <FiArrowUp />
                                Back to top
                            </motion.button>
                        </div>
                    </div>
                </div>
            </section>

            <ClientFooter />
        </div>
    );
};

export default PP;
